/*
  Subtest results for a given source agent
*/
import { action, computed, observable } from 'mobx';
import api from 'core/util/api';
import { queryClient } from 'app/queryClient';
import AgentTargetTestResultsState from './AgentTargetTestResultsState';

// whether the site has any active devices visible to the user
const siteHasActiveDevices = (siteId) =>
  queryClient.fetchQuery({
    queryKey: ['siteHasActiveDevices', String(siteId)],
    queryFn: () =>
      api
        .get('/api/ui/lookups/devices', { query: { limit: 1, siteId }, showErrorToast: false })
        .then((page) => !!page?.devices?.length),
    staleTime: 30 * 1000
  });

class AgentTestResultsState {
  get defaults() {
    return {
      // state data
      agent: null,
      test: null,
      targets: observable.map({}, { deep: false }),
      tabId: 'metrics',
      // loading indicators
      loadingTraceResults: false,
      // results indicators
      hasResults: false,
      hasTraceResults: false,
      // returned results
      results: null,
      traceResults: null
    };
  }

  // AgentModel
  agent = this.defaults.agent;

  // TestModel
  test = this.defaults.test;

  // map of AgentTargetTestResultsState keyed by target
  @observable.ref
  targets = this.defaults.targets;

  // determines the selected data tab in the subtest test results ui
  @observable
  tabId = this.defaults.tabId;

  @observable
  loadingTraceResults = this.defaults.loadingTraceResults;

  @observable
  hasResults = this.defaults.hasResults;

  @observable
  hasTraceResults = this.defaults.hasTraceResults;

  // subtest results
  @observable.ref
  results = this.defaults.results;

  // subtest trace results
  @observable.ref
  traceResults = this.defaults.traceResults;

  // whether a private agent's site has devices
  @observable
  privateSiteHasDevices = false;

  constructor({ store, test, agent }) {
    this.store = store;
    this.test = test;
    this.agent = agent;

    if (test.isTransaction) {
      this.tabId = 'results';
    }

    this.configure();
    this.loadPrivateSiteDevices();
  }

  // re-checks whether the agent's site has devices; the lookup is cached, so calling this on each results refresh is cheap
  loadPrivateSiteDevices() {
    const { privateSiteId } = this;

    if (!privateSiteId) {
      return;
    }

    siteHasActiveDevices(privateSiteId)
      .then(
        action((hasDevices) => {
          this.privateSiteHasDevices = hasDevices;
        })
      )
      .catch(
        action(() => {
          this.privateSiteHasDevices = false;
        })
      );
  }

  @action
  configure() {
    const { $exports } = this.store;
    $exports.getSettings().then(
      action(({ hashedTabId }) => {
        if (hashedTabId !== undefined) {
          this.tabId = hashedTabId;
        }
      })
    );
  }

  @action
  setTestResults(testResults, results, aggregateResults) {
    this.loadPrivateSiteDevices();
    const hasResults = this.checkForTestResults(results);

    if (hasResults) {
      const targets = Object.keys(results.targets);

      this.results = results;
      for (let i = 0; i < targets.length; i += 1) {
        const target = targets[i];

        this.setupTargetResults(target);
        this.targets
          .get(target)
          .setTestResults(testResults, results.targets[target], aggregateResults?.targets[target]);
      }
    }

    this.hasResults = hasResults;
  }

  @action
  setTraceResults(traceResults, results) {
    this.traceResults = results;
    const hasTraceResults = this.checkForTraceResults(results);

    if (hasTraceResults) {
      const targets = Object.keys(results.targets);

      for (let i = 0; i < targets.length; i++) {
        const target = targets[i];

        this.setupTargetResults(target);
        this.targets.get(target).setTraceResults(traceResults, results.targets[target]);
      }
    } else {
      // clear out all the target trace results
      this.targets.forEach((targetState) => targetState.setTraceResults());
    }

    this.hasTraceResults = hasTraceResults;
  }

  // initializes AgentTargetTestResultsState for the given target
  @action
  setupTargetResults(target) {
    if (!this.targets.get(target)) {
      this.targets.set(
        target,
        new AgentTargetTestResultsState({
          store: this.store,
          test: this.test,
          agent: this.agent,
          target
        })
      );
    }
  }

  // ----- class utils ------------------------------------------------

  checkForTestResults({ overall = {} } = {}) {
    return !!Object.keys(overall).length;
  }

  checkForTraceResults(results) {
    return !!Object.keys(results?.targets || {}).length;
  }

  // ----- computed properties ------------------------------------------------

  // flow queries for private agents depend on their site having devices
  @computed
  get privateSiteId() {
    const siteId = this.agent?.get('site_id');
    return this.agent?.get('agent_type') === 'private' && siteId && siteId !== '0' ? siteId : undefined;
  }

  @computed
  get allowFlowQuery() {
    const { $devices } = this.store;

    if (!$devices.hasReceivedAnyFlow) {
      return false;
    }

    // don't do flow query if private agent site has no devices, otherwise allow.
    return !this.privateSiteId || this.privateSiteHasDevices;
  }

  // ----- change handlers ------------------------------------------------

  @action
  onPageTabChange = (id) => {
    this.tabId = id;
    if (this.test.agentResults.displayOptions.allowSettings) {
      this.store.$exports.setHash({ hashedTabId: id }, true, true); // merge and replace the querystring to just be the q hash
    }
  };
}

export default AgentTestResultsState;
